import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { AddOnRowModel } from './addOnRowModel';
import { tierChipLabel, tierHint } from './addOnFormat';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the add-ons panel; each part uses its own slice of it */
import styles from './MonthlyAddOns.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddOnSliderRowProps {
  model: AddOnRowModel;
  name: string;
  sub: string;
  /** "260 posts a month in all (60 with Artist)". */
  facts: string;
  /** "$17.00 a month". */
  price: string;
  disabled: boolean;
  onChange: (quantity: number) => void;
  /** The word after the value; "a month" when left out ("artists" for the artists row). */
  unit?: string;
  /** The screen reader text of the value; the add-on text when left out. */
  valueText?: string;
  /** The line under the facts: the floor of the artists row (design a5). */
  note?: string | null;
  /** The hint line; the tier hint when left out. */
  hint?: string | null;
}

/**
 * One add-on: the value, the slider with the tier zones, the tier chips, the facts and the hint
 * (design monthly-addons a1). The artists row (a5) adds the hatched floor and its note; the slider
 * never goes below `model.min`.
 */
const AddOnSliderRowComponent: React.FC<AddOnSliderRowProps> = ({
  model,
  name,
  sub,
  facts,
  price,
  disabled,
  onChange,
  unit,
  valueText,
  note,
  hint: hintOverride,
}) => {
  const intl = useIntl();
  const inputId = `addon-${model.kind}`;
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(Math.max(model.min, Number(event.target.value))),
    [onChange, model.min]
  );
  const hint = hintOverride === undefined ? tierHint(intl, model) : hintOverride;
  const sliderStyle = {
    '--addon-percent': `${model.percent}%`,
    '--addon-floor': `${model.floorPercent}%`,
  } as React.CSSProperties;

  return (
    <div className={styles.row} data-testid={`addon-row-${model.kind}`}>
      <div className={styles.top}>
        <div>
          <label className={styles.name} htmlFor={inputId}>
            {name}
          </label>
          <div className={styles.sub}>{withBrandWord(sub)}</div>
        </div>
        <div
          className={clsx(styles.value, model.quantity === 0 && styles.valueZero)}
          data-testid={`addon-value-${model.kind}`}
        >
          {intl.formatMessage(messages.value, { count: model.quantity })}
          <small>{unit ?? intl.formatMessage(messages.aMonth)}</small>
        </div>
      </div>
      <div className={styles.slider} style={sliderStyle}>
        <div className={styles.track} aria-hidden="true">
          {model.zonePercents.map((width) => (
            <div key={width} className={styles.zone} style={{ width: `${width}%` }} />
          ))}
        </div>
        <div className={styles.fill} aria-hidden="true" />
        {model.floorPercent > 0 && (
          <div className={styles.floor} aria-hidden="true" data-testid={`addon-floor-${model.kind}`} />
        )}
        <input
          id={inputId}
          className={styles.range}
          type="range"
          min={0}
          max={model.max}
          step={model.step}
          value={model.quantity}
          disabled={disabled}
          onChange={handleChange}
          aria-valuetext={valueText ?? intl.formatMessage(messages.valueText, { count: model.quantity })}
        />
      </div>
      <div className={styles.ends} aria-hidden="true">
        <span>0</span>
        <span>{intl.formatNumber(model.max)}</span>
      </div>
      <div className={styles.tiers}>
        {model.tiers.map((chip) => (
          <span key={chip.from} className={clsx(styles.tier, chip.on && styles.tierOn)}>
            {tierChipLabel(intl, chip, model.kind)}
          </span>
        ))}
      </div>
      <div className={styles.facts}>
        <span>{facts}</span>
        <span className={styles.fig} data-testid={`addon-price-${model.kind}`}>
          {price}
        </span>
      </div>
      {note !== undefined && note !== null && (
        <div className={styles.note} data-testid={`addon-note-${model.kind}`}>
          {note}
        </div>
      )}
      {hint !== null && <div className={styles.hint}>{withBrandWord(hint)}</div>}
    </div>
  );
};

export const AddOnSliderRow = TypedMemo(AddOnSliderRowComponent);
